import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import { getSchedules, getScheduleById } from "./api"

const initialState = {
  byId: {}, // A map of schedules by id
  priorities: {
    0: {
      id: 0,
      name: "low",
    },
    1: {
      id: 1,
      name: "high",
    },
  },
  matchesByCtx: {
    // root context powering the schedules list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // schedules by id
      page: {}, // Pagination information
    },
    /*
      Example of other contexts
      "collection/123": {
        ids: [],
        page: {}, // Pagination information
      },
    */
  },
  /*
    example of _loadStatus pattern for a single object
    byId: {
      '1': {
        id: '1',
        _loadStatus: 'idle',
      }
    }
  */
}

/**
 * Thunk to perform async logic to fetch schedules's
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchSchedules = createAsyncThunk(
  "schedules/fetch",
  /**
   *
   * @param filter filters to apply to the schedules search
   * @param page Pagination information
   * @param context
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getSchedules({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to fetch latest schedule for astrosite
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchLatestScheduleForAstrosite = createAsyncThunk(
  "schedules/fetch",
  async (astrositeId) => {
    return await getSchedules({
      filter: {
        astrosite_id: astrositeId,
      },
      page: {
        offset: 0,
        limit: 1,
      },
    })
  }
)

/**
 * Thunk to perform async logic to fetch a schedule
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchScheduleById = createAsyncThunk(
  "schedule/fetch",
  async (scheduleId) => {
    return await getScheduleById(scheduleId)
  }
)

export const schedulesSlice = createSlice({
  name: "schedules",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clearMatches: (state, action) => {
      state.matchesByCtx[action?.payload || "root"].ids = []
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchedules.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          // Don't reset if we are paging
          if (action.payload.page && action.payload.page.start_index === 0) {
            state.matchesByCtx[context].ids = []
          }
          action.payload.data &&
            action.payload.data.map((schedule) => {
              // Add the schedule to state by id (this can include ids not retrieved in this call)
              state.byId[schedule.id] = schedule
              state.matchesByCtx[context].ids.push(schedule.id)
              state.matchesByCtx[context].page = action.payload.page
            })
        }
      })
      .addCase(fetchSchedules.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(fetchScheduleById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchScheduleById.fulfilled, (state, action) => {
        if (action.payload && action.payload.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._loadStatus = "idle"
        }
      })
      .addCase(fetchScheduleById.rejected, (state, action) => {
        state.byId[action.meta.arg]._loadStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage += action.payload
      })
  },
})

// Selector for all schedules
export const selectSchedulesById = (state) => state.schedules.byId
// Selector for possible priorities
export const selectSchedulePriorities = (state) => state.schedules.priorities
/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectSchedulesByContext(state, `rso/${id}`))
 */
export const selectSchedulesByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.schedules.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`schedules, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectSchedulesByIds(state, ["1", "2"]))
 */
export const selectSchedulesByIds = createSelector(
  [
    // first input - extract value from `state`
    selectSchedulesById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)
export const { clearMatches } = schedulesSlice.actions

export default schedulesSlice.reducer
